import { PALETTE, paletteRgba } from '../designPalette';
import { FOUNDATIONS } from '../designFoundations';
import { DESIGN_RECIPES } from '../designRecipes';
import { CONTROL_PROFILES } from '../controlProfiles';
import { typographyCss } from '../ui/typographyCss';
/**
 * The ONE toast stylesheet for the three hand-rolled surfaces.
 *
 * Emily's call (board P0, 2026-09-14): the extension, the guest client review
 * and the team-review drawer share one hand-rolled toast; the dashboard keeps
 * Sonner. See `./toastKind.ts` for the gate evidence behind that split.
 *
 * A STRING rather than a `.css` file because the guest bundle has no module
 * system at all (zero `import`/`require` across its 57 sections, built by
 * straight concatenation), so the only thing it can consume is text. The
 * extension appends it to its shadow root beside the two stylesheets
 * `ensureStyleElements()` already injects there.
 *
 * Positioning is deliberately UNCHANGED (bottom centre). The row sketched
 * bottom-right to match Sonner, but the extension's sidebar owns the right
 * 340px of the viewport, so a bottom-right toast would sit under it. That is a
 * design call with a real collision behind it and it is Emily's, not a
 * side effect of unifying the renderers.
 */
import { TOAST_DURATION_VAR } from './toastKind.js';
/**
 * How many steps the bar takes under reduced motion.
 *
 * Eight reads as a discrete countdown rather than a slide while still showing
 * roughly how long is left. The pre-fix sheet set `opacity: 0` here, which
 * removed the information along with the movement. Reduced motion means less
 * MOVEMENT, never less information.
 */
export const TOAST_BAR_STEPS = 8;
// Pure construction lets scanner bundles omit unused presentation CSS.
export const TOAST_CSS = /* @__PURE__ */ (() => {
    // Embedded controls use fixed pixels so a host page cannot resize them.
    const actionHeight = `${parseFloat(CONTROL_PROFILES.app.compact.height) * 16}px`;
    const dismissSize = `${parseFloat(CONTROL_PROFILES.app.small.height) * 16}px`;
    // The capture toolbar's buttons are the standard app control (captureToolbarDom.ts controlHeight).
    const toolbarControlHeight = `${parseFloat(CONTROL_PROFILES.app.standard.height) * 16}px`;
    // ds-hand-rolled: the existing 400px readable toast width, viewport stacking
    // order and 1px visually-hidden announcer geometry are structural exceptions.
    return `
/* ----------------------------------------
   Block: oq-toast, shared by extension, guest review and team review.
   The extension injects this into its shadow root; the review surfaces get it
   from the server, which appends it to REVIEW_PAGE_CSS. One source, no copy.
   ---------------------------------------- */
.oq-toast-container {
  position: fixed;
  bottom: ${FOUNDATIONS.space[5]};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: ${FOUNDATIONS.space[2]};
  z-index: 2147483647;
  max-width: 400px;
  width: calc(100% - ${FOUNDATIONS.space[10]});
  pointer-events: none;
}

/* While a capture toolbar shows, the stack sits above it instead of across its
   right end, where it covered the Highlights eye, its caret and the collapse
   button for its few seconds (Emily, 2026-10-27, decision 12 of the last-week
   P3 sweep). The toolbar sits space[12] from the bottom; the lift is that
   offset plus the standard control plus three space[3] steps. Measured in real
   Chromium 2026-10-27 (verify-toast-above-toolbar): the toolbar is 52px tall on
   both surfaces, so the stack clears it by 20px. The two roots a toolbar and
   the stack share are the
   extension's shadow container and the review page's body; a hidden toolbar
   (aria-hidden while the create popover owns the canvas) does not lift. */
:where(body, #overlayqa-overlay-container):has(.oq-capture-toolbar:not([aria-hidden="true"])) .oq-toast-container {
  bottom: calc(${FOUNDATIONS.space[12]} + ${toolbarControlHeight} + ${FOUNDATIONS.space[3]} * 3);
}

/* The two live regions the container keeps mounted. Visually hidden rather
   than display:none or visibility:hidden, both of which also remove the node
   from the accessibility tree and would silence the very announcement this
   exists to make. */
.oq-toast-announcer {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* position/overflow are load-bearing, not cosmetic: the bar is absolutely
   positioned, so without a positioned ancestor it resolves against the fixed
   container and renders OUTSIDE the toast. Measured in Chromium 2026-09-14:
   every numeric assertion about the bar still passed while it did. */
.oq-toast {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: ${FOUNDATIONS.space[3]};
  padding: ${FOUNDATIONS.space[3]} ${FOUNDATIONS.space[4]};
  background: var(--oq-brand);
  border-radius: ${FOUNDATIONS.radius.panel};
  box-shadow: ${FOUNDATIONS.shadow.popover};
  color: var(--oq-white);
  ${typographyCss('body')};
  font-family: ${FOUNDATIONS.fontFamily.system};
  pointer-events: auto;
  animation: oq-toast-slide-in ${DESIGN_RECIPES.motion.transition} ${DESIGN_RECIPES.motion.easeOut};
}

.oq-toast--success { background: var(--oq-brand); }
.oq-toast--error { background: var(--oq-error); }
.oq-toast--warning {
  background: var(--oq-warning);
  /* Warning is the ONE kind that cannot take the block's white text. Measured
     with the repo's own contrastRatio(): white on --oq-warning (#f59e0b) is
     2.15:1, a WCAG AA failure, and even --oq-warning-dark only reaches 3.19:1.
     Dark on the same amber is 7.95:1. The other three kinds clear AA on white
     (success 4.69, error 4.83, info 6.42) and are left alone. */
  color: var(--oq-gray-900);
}
.oq-toast--info { background: var(--oq-brand-hover); }

.oq-toast__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oq-toast__message {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  ${typographyCss('body')};
  /* A toast wraps inside a narrow (max 400px) container with no line-clamp, so
     a long message can leave a single word stranded on its last line. */
  text-wrap: pretty;
}

.oq-toast__action {
  flex-shrink: 0;
  box-sizing: border-box;
  height: ${actionHeight};
  padding: 0 ${FOUNDATIONS.space[2]};
  background: var(--oq-white);
  border: none;
  border-radius: ${FOUNDATIONS.radius.control};
  color: var(--oq-brand);
  ${typographyCss('label')};
  font-weight: ${FOUNDATIONS.fontWeight.semibold};
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.oq-toast__action:hover { opacity: 0.85; }
.oq-toast--error .oq-toast__action { color: var(--oq-error); }
/* Same reason, mirrored: the action sits on a white chip, so amber-on-white
   would be the identical 2.15:1 failure. */
.oq-toast--warning .oq-toast__action { color: var(--oq-gray-900); }

.oq-toast__dismiss {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${dismissSize};
  height: ${dismissSize};
  padding: 0;
  background: transparent;
  border: none;
  color: inherit;
  border-radius: ${FOUNDATIONS.radius.sm};
  ${typographyCss('label')};
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}

.oq-toast__dismiss:hover {
  color: var(--oq-white);
  background: var(--oq-dark-surface-border);
}

.oq-toast--warning .oq-toast__dismiss:hover { color: var(--oq-gray-900); }
.oq-toast__action:focus-visible, .oq-toast__dismiss:focus-visible {
  outline: ${DESIGN_RECIPES.border.focus} solid currentColor;
  outline-offset: ${FOUNDATIONS.space[1]};
}
.oq-toast__action:focus-visible { outline-color: var(--oq-white); }
.oq-toast--warning .oq-toast__action:focus-visible { outline-color: var(--oq-gray-900); }

/* Countdown bar: the window, drawn along the bottom edge.
   Driven by ${TOAST_DURATION_VAR} so it tracks THIS toast's window; a fixed
   duration here would be wrong for every error (7s) and every Undo (10s). */
.oq-toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: ${FOUNDATIONS.space[1]};
  width: 100%;
  background: ${paletteRgba(PALETTE.surface.card, 0.35)};
  border-radius: 0 0 ${FOUNDATIONS.radius.panel} ${FOUNDATIONS.radius.panel};
  transform-origin: left;
  animation: oq-toast-progress linear forwards;
  animation-duration: var(${TOAST_DURATION_VAR}, 4000ms);
}

@keyframes oq-toast-progress {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* The bar follows the TIMER, so both things that stop the timer stop the bar.
   Hover is container-wide, not per-toast, because the dismiss timer pauses for
   the whole stack: pausing only the hovered bar would show three toasts with
   one bar frozen and two running, which misreports what is happening. */
.oq-toast-container:hover .oq-toast__progress {
  animation-play-state: paused;
}

/* Set by the container while the tab is hidden. A backgrounded toast that
   expired unseen was never shown at all. */
.oq-toast-container[data-oq-toast-paused] .oq-toast__progress {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  .oq-toast { animation: none; }
  /* STEPS rather than hides: the countdown is information, and reduced motion
     asks for less movement, not less information. */
  .oq-toast__progress {
    animation-timing-function: steps(${TOAST_BAR_STEPS}, end);
  }
}
`;
})();
//# sourceMappingURL=toastStyles.js.map